import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';

import { createFluidSim } from './FluidSim';
import { createOrbFlare } from './OrbFlare';
import { createOrbParticles } from './OrbParticles';
import { BG_FRAG, BG_VERT, ORB_FRAG, ORB_VERT } from './orbShaders';

/**
 * 히어로 오브의 <b>씬</b> — 껍데기 없이 three.js 만 있는 부분 (2026-09-18 분리).
 *
 * <h2>왜 떼어냈나</h2>
 * 앱(레노버 탭)에도 같은 오브를 세우기로 했는데(사용자 지시), RN 에는 `<canvas>` 도
 * `ResizeObserver` 도 `pointermove` 도 없다. 반대로 <b>씬은 그 셋을 하나도 안 쓴다</b> — 카메라
 * 화각 30°·좌표 `(0, 3.1, 16.6)`·유니폼 수십 개·거리 페이드 `18.18‥19.53` 는 전부 역산으로 맞춘
 * 값이라, 옮겨 적는 순간 두 벌이 갈라진다.
 *
 * <p>그래서 <b>씬은 한 벌</b>로 두고 껍데기만 둘을 둔다:
 *  - 웹 — {@link HeroOrb} (`<div>` + `<canvas>` + ResizeObserver + pointermove)
 *  - 앱 — `mobile/src/ui/HeroOrbGl.tsx` (`expo-gl` 의 `GLView` + 터치)
 *
 * <p>여기 있는 코드는 <b>옮겨 적은 것이 아니라 잘라 옮긴 것</b>이다. 값이 하나도 바뀌지 않았다.
 *
 * <h2>껍데기가 해야 하는 일</h2>
 * 렌더러를 만들어 넘기고({@link OrbSceneOptions.renderer}), 크기가 바뀌면 {@link OrbScene.resize},
 * 손/커서가 움직이면 {@link OrbScene.setPointer}, 매 프레임 {@link OrbScene.frame} 을 부른다.
 * 그림을 언제 보여 줄지도 껍데기 몫이다 — {@link OrbScene.frames} 가 2 가 된 뒤가 안전하다.
 */

/**
 * 배경 네 색과 위쪽 밝기.
 *
 * <p>16진수로 주고받는 이유는 하나다 — 고를 때 눈으로 읽을 수 있어야 한다. 실측값은 소수
 * 열여섯 자리로 적혀 있는데 그걸 보고 «산호색» 을 떠올릴 사람은 없다. 값이 달라지지는
 * 않는다({@link hex} 머리말).
 */
export type HeroBg = {
  /** 아래쪽 바탕. */
  color1: string;
  /** 그 위에 섞이는 둘째 색. */
  color2: string;
  /** 위쪽(밝은 쪽). */
  color3: string;
  /** 가장 밝은 얼룩. `color3` 과 섞여 위쪽 하이라이트를 만든다. */
  color4: string;
  /** `color3` 만 누르는 밝기. 1 이면 그대로. */
  color3Brightness: number;
};

/** 구체 자체의 색. `Landing Orb` 유니폼 중 색인 것들이다. */
export type HeroOrbTone = {
  /** 구체의 바탕빛. 이 색이 구체의 «몸 색» 을 정한다. */
  ambient: string;
  /** 구름 얼룩이 지는 자리에 `ambient` 에 곱해지는 색. 얼룩을 어느 쪽으로 물들일지. */
  clouds: string;
  /** 테두리. <b>바깥으로 갈수록 이 색이 된다</b> — 구체를 감싸는 빛 띠가 이것이다. */
  fresnel: string;
  /** 조명 둘. 구체 표면의 밝은 면과 반사광. */
  light: string;
  light2: string;
};

/** 히어로 한 벌의 색 전부. 칸마다 다른 벌을 준다. */
export type HeroLook = {
  bg: HeroBg;
  orb: HeroOrbTone;
  /**
   * 알갱이·섬광을 이 색 <b>계열로</b> 옮긴다. `null` 이면 원본 팔레트 그대로다.
   *
   * <p>색을 통째로 갈아 끼우지 않고 «계열만» 옮기는 이유 — 알갱이 팔레트는 열한 색이
   * 어두운 것부터 흰 것까지 층을 이루고 있고, 그 층이 알갱이의 입체감이다. 전부 한 색으로
   * 칠하면 빨간 점 뭉치가 된다. 그래서 <b>밝기는 그대로 두고 색상만</b> 옮긴다 —
   * 어두운 것은 짙은 적갈색이 되고 흰 것은 흰 채로 남는다.
   */
  particleTint: string | null;
};

/**
 * 원본 씬 파일의 실측값 한 벌. <b>16진수로 적었지만 값은 바뀌지 않았다</b> —
 * {@link hex} 가 sRGB 변환 없이 그대로 읽으므로 `#ff5050` 이 곧 `(1, .3137, .3137)` 이다.
 */
export const HERO_LOOK_SOURCE: HeroLook = {
  bg: {
    color1: '#ff5050',
    color2: '#d2ae41',
    color3: '#faf4c9',
    color4: '#fffae8',
    /* [원본 1] 배경의 밝은 쪽(위)이 크림을 넘어 흰색에 가까워지면 그 앞을 지나는
       흰 알갱이가 묻힌다. 요청대로 한 칸 낮춰 알갱이가 읽히게 한다. */
    color3Brightness: 0.88,
  },
  orb: {
    ambient: '#ffa077',
    clouds: '#db1010',
    fresnel: '#f5e5e2',
    light: '#e7b56b',
    light2: '#b82e51',
  },
  particleTint: null,
};

/**
 * 16진수를 <b>변환 없이</b> 성분으로 읽는다.
 *
 * <p>`new THREE.Color('#ff5050')` 로 쓰면 three 가 sRGB → 선형 변환을 걸어
 * `(1, .08, .08)` 이 된다 — 실측값 `(1, .3137, .3137)` 과 전혀 다른 색이다. 셰이더가
 * 이 값을 색으로 곧장 쓰므로 변환이 끼면 안 된다.
 */
const hex = (value: string) =>
  new THREE.Color().setHex(Number.parseInt(value.replace('#', ''), 16), THREE.LinearSRGBColorSpace);

/** 구체의 «세계 크기». `SphereGeometry(1,…)` 를 3 배로 키웠으니 지름 6 이다. */
const ORB_WORLD_DIAMETER = 6;

export type OrbSceneOptions = {
  /** 껍데기가 만든 렌더러. 웹은 `WebGLRenderer`, 앱은 `expo-gl` 컨텍스트로 만든 같은 것이다. */
  renderer: THREE.WebGLRenderer;
  look?: HeroLook;
  /** 구체가 <b>몇 px 로</b> 보일지. 안 주면 원본 화각 그대로다. */
  orbSize?: number;
  background?: boolean;
  particles?: boolean;
  flare?: boolean;
  /**
   * 구체를 <b>세울지</b> (2026-09-18).
   *
   * <p>끄면 배경(스카이돔)만 남는다 — 앱 하단 바가 그 판만 쓴다. 구체는 이 씬의 주인공이라
   * 끄는 일이 이상해 보이지만, <b>배경도 같은 씬의 일부</b>라서 그것만 쓰려면 나머지를 접는 수밖에
   * 없다. 다른 셋과 같은 이유로 유니폼이 아니라 «짓느냐 마느냐» 다.
   */
  orb?: boolean;
  /** 알갱이 한 톨의 크기. 작은 판에서 올린다 — {@link OrbParticlesOptions.size}. */
  particleSize?: number;
  /**
   * 알갱이 시뮬 격자 한 변 (2026-09-21). 안 주면 원본 256(=65,536 톨)이다.
   *
   * <h2>왜 열었나 — 이 비용은 그리는 크기와 무관하다</h2>
   * 알갱이 수는 화면에 몇 px 로 그리든 <b>같다</b>. 그래서 앱 하단 바의 68dp 구슬이 1,280px
   * 히어로와 똑같이 65,536 톨을 매 프레임 시뮬하고 인스턴스로 그렸다.
   *
   * <p>줄이면 <b>표면이 성겨진다.</b> 톨 수를 4분의 1로 내리면 덮는 넓이도 4분의 1이라, 아무것도
   * 안 하면 「알갱이가 없는」 그림이 된다(그 함정은 2026-09-18 에 한 번 겪었다). 그래서 부르는
   * 쪽이 {@link #particleSize} 를 같은 비율로 올려 넓이를 맞춘다 — {@link particleSizeFor} 가 그 셈을 한다.
   */
  particleGrid?: number;
  /** 유체 격자 한 변. 안 주면 원본 128 — {@link FluidSimOptions}. */
  fluidSize?: number;
  /** 유체 압력 반복. 안 주면 원본 20 — {@link FluidSimOptions}. */
  fluidIterations?: number;
};

export type OrbScene = {
  /** 칸 크기가 바뀌었을 때. 픽셀 비율은 껍데기가 정한다(웹은 devicePixelRatio, 앱은 1). */
  resize(width: number, height: number, pixelRatio: number): void;
  /** 유체를 미는 자리. <b>GL 좌표</b>(아래가 0)로 0‥1 이다. */
  setPointer(x: number, y: number, aspect: number): void;
  /** 색은 프레임마다 따라간다 — 씬을 다시 태우지 않는다. */
  setLook(next: HeroLook): void;
  setOrbSize(next?: number): void;
  /** 한 프레임. `dtMs` 는 지난 프레임과의 간격(ms)이다. */
  frame(dtMs: number): void;
  /** 지금까지 그린 프레임 수. 껍데기가 «언제 보여 줄지» 를 이걸로 정한다. */
  readonly frames: number;
  dispose(): void;
};

/** 알갱이 원본 크기. 웹 히어로가 수백 px 로 설 때 제값을 하는 값이다. */
export const PARTICLE_BASE_SIZE = 0.35;

/** 알갱이 원본 크기가 제값을 하는 판의 변 길이(CSS px). */
const PARTICLE_REFERENCE_PX = 280;

/** 아무리 작은 판이어도 이 배를 넘겨 키우지 않는다 — 넘기면 톨이 아니라 얼룩이 된다. */
const PARTICLE_MAX_SCALE = 3;

/**
 * 판 크기에 맞춘 알갱이 크기 — <b>작은 칸일수록 톨을 키운다</b>.
 *
 * <p>개수는 판과 무관하게 65,536 톨이다. 그런데 76px 짜리 칸에서는 그 톨들이 서로 겹쳐 매끈한
 * 표면이 되고, 밝은 2%만 점으로 남아 «성긴 흰 점 몇 개» 로 보인다(앱 실측 2026-09-18). 그래서
 * 판이 작은 만큼 톨을 키운다.
 *
 * <p><b>웹과 앱이 이 한 함수를 함께 쓴다.</b> 앱 웹뷰(`orbStandalone`)가 먼저 이 보정을 넣었고
 * 웹 AI 버블에는 없어서, 같은 씬인데 앱이 더 또렷했다 — 규칙이 두 벌이면 그런 차이가 생긴다.
 */
export function particleSizeFor(boxPx: number): number {
  const size = Math.max(1, boxPx);
  return PARTICLE_BASE_SIZE * Math.min(PARTICLE_MAX_SCALE, Math.max(1, PARTICLE_REFERENCE_PX / size));
}

export function createOrbScene({
  renderer,
  look = HERO_LOOK_SOURCE,
  orbSize,
  background = true,
  particles = true,
  flare = true,
  orb: withOrb = true,
  particleSize,
  particleGrid,
  fluidSize,
  fluidIterations,
}: OrbSceneOptions): OrbScene {
  /* 색과 크기는 «지금 값» 을 프레임마다 읽어 간다 — 원본 컴포넌트의 ref 와 같은 자리다. */
  const lookRef = { current: look };
  const orbSizeRef = { current: orbSize };

    const fluid = createFluidSim(renderer, { size: fluidSize, iterations: fluidIterations });
    const fluidTexture = () => fluid.texture;

    const scene = new THREE.Scene();
    /* 카메라는 원본 씬 파일 값 그대로여야 한다. 당기면 안 된다 —
       입자의 `uDistanceFadeNearFar (18.18, 19.53)` 와 `uParticleSize` 가 이 «세계 거리»
       기준으로 맞춰져 있어서, 당기는 순간 거리 페이드가 꺼지고 알갱이가 두 배로 커진다.
       화각은 코드에 없어 원본 캡처에서 역산했다(구 반지름 3 이 화면 높이의 0.70). */
    const camera = new THREE.PerspectiveCamera(30, 1, 0.1, 200);
    camera.position.set(0, 3.1000000000000014, 16.599999999999987);
    camera.lookAt(0, 0, 0);

    /* ── 배경: 평면이 아니라 «스카이돔» 이다.
       원본은 `SphereGeometry(1,12,12)` 를 15 배로 키우고 `BackSide` 로 안쪽을 본다.
       카메라가 그 안쪽의 «일부» 만 보므로 노이즈가 크게 늘어나 매끈한 그라디언트가 된다.
       내가 화면에 딱 맞는 사각형에 그렸을 때 얼룩덜룩했던 것은 노이즈가 통째로
       보였기 때문이다 — 셰이더가 아니라 «어디에 그리느냐» 의 문제였다. */
    const bgUniforms = {
      tFluidSim: { value: fluid.texture },
      uColor1: { value: hex(HERO_LOOK_SOURCE.bg.color1) },
      uColor2: { value: hex(HERO_LOOK_SOURCE.bg.color2) },
      uColor3: { value: hex(HERO_LOOK_SOURCE.bg.color3) },
      uColor4: { value: hex(HERO_LOOK_SOURCE.bg.color4) },
      uColor4BlendStart: { value: 0 },
      uColor4BlendEnd: { value: 1 },
      uColor1Brightness: { value: 1 },
      uColor2Brightness: { value: 1 },
      uColor3Brightness: { value: HERO_LOOK_SOURCE.bg.color3Brightness },
      uOffset1: { value: new THREE.Vector3(-3.66000000000001, -0.3999999999999997, 0) },
      uOffset2: { value: new THREE.Vector3(-0.5100000000000005, -0.27000000000000035, 0) },
      uOffset3: { value: new THREE.Vector2(0, 0) },
      uScale1: { value: 1.8800000000000008 },
      uScale2: { value: 1.4400000000000004 },
      uScale3: { value: 1 },
      uSpeed: { value: 0.1 },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uTime: { value: 0 },
    };
    /* 배경을 끄면 <b>돔을 아예 짓지 않는다</b> — 투명하게 그려 두면 픽셀은 그대로 다 계산된다.
       renderer 가 `alpha: true` 라 지우는 색의 알파가 0 이고, 그래서 캔버스가 통째로 비친다. */
    const bgMesh = background
      ? new THREE.Mesh(
          new THREE.SphereGeometry(1, 12, 12),
          new THREE.ShaderMaterial({
            vertexShader: BG_VERT,
            fragmentShader: BG_FRAG,
            uniforms: bgUniforms,
            side: THREE.BackSide,
            transparent: true,
            depthWrite: false,
          }),
        )
      : null;
    if (bgMesh) {
      bgMesh.scale.setScalar(15);
      bgMesh.renderOrder = -1;
      scene.add(bgMesh);
    }

    // ── 구름 패스와 본 패스는 «같은 셰이더» 다. RENDER_CLOUDS 정의만 다르다(원본 그대로).
    const cloudsTarget = new THREE.WebGLRenderTarget(1, 1, { depthBuffer: false });
    const sphereGeo = new THREE.SphereGeometry(1, 32, 32);

    const cloudsUniforms = {
      uCloudNoiseScale: { value: new THREE.Vector3(2.5, 2.5, 2.5) },
      uCloudNoiseSpeed: { value: 0.4199999999999998 },
      uTime: { value: 0 },
    };
    const cloudsScene = new THREE.Scene();
    const cloudsMesh = new THREE.Mesh(
      sphereGeo,
      new THREE.ShaderMaterial({
        vertexShader: ORB_VERT,
        fragmentShader: ORB_FRAG,
        uniforms: cloudsUniforms,
        defines: { RENDER_CLOUDS: '' },
      }),
    );
    cloudsMesh.scale.setScalar(3);
    cloudsScene.add(cloudsMesh);

    const orbUniforms = {
      tClouds: { value: cloudsTarget.texture },
      uTime: { value: 0 },
      uColorFresnelAmount: { value: 3.400000000000001 },
      uColorFresnelOffset: { value: 0.05 },
      uColorFresnelFalloff: { value: 1.5100000000000005 },
      uOpacityFresnelAmount: { value: 1.3699999999999974 },
      uOpacityFresnelOffset: { value: 0.025000000000000112 },
      uOpacityFresnelFalloff: { value: 1.980000000000002 },
      uCloudsSmoothstep: { value: new THREE.Vector2(0.36000000000000015, 0.9199999999999999) },
      uCloudsColor: { value: hex(HERO_LOOK_SOURCE.orb.clouds) },
      uOpacity: { value: 0.95 },
      uFresnelColor: { value: hex(HERO_LOOK_SOURCE.orb.fresnel) },
      uAmbientColor: { value: hex(HERO_LOOK_SOURCE.orb.ambient) },
      uLightPosition: { value: new THREE.Vector3(-12, 16, 53) },
      uLightColor: { value: hex(HERO_LOOK_SOURCE.orb.light) },
      uLightIntensity: { value: 0.9499999999999996 },
      uSpecularIntensity: { value: 0.18999999999999984 },
      uLightPosition2: { value: new THREE.Vector3(18.000000000000007, -6.700000000000017, 22.000000000000057) },
      uLightColor2: { value: hex(HERO_LOOK_SOURCE.orb.light2) },
      uLightIntensity2: { value: 0.9200000000000004 },
      uSpecularIntensity2: { value: 0.6799999999999992 },
      uShininess: { value: 74 },
      uDebugClouds: { value: false },
    };
    const orb = !withOrb ? null : new THREE.Mesh(
      sphereGeo,
      new THREE.ShaderMaterial({
        vertexShader: ORB_VERT,
        fragmentShader: ORB_FRAG,
        uniforms: orbUniforms,
        transparent: true,
        depthWrite: true,
      }),
    );
    if (orb) {
      orb.scale.setScalar(3);
      scene.add(orb);
    }

    /*
      <b>알맹이</b> — 배경을 껐을 때만 선다.

      <p>구체 재질은 반투명이다(`uOpacity .95` 에 프레넬 불투명도 falloff 까지). 뒤에 스카이돔이
      있을 때는 그것이 몸이 되어 주므로 티가 안 나는데, 돔을 치우면 <b>뒤에 있는 화면이 구체를
      통과해 비친다</b> — 표 위에 띄우면 숫자가 구슬 안으로 들여다보인다(2026-09-17 캡처).

      <p>그래서 조명도 무늬도 없는 <b>불투명한 구</b>를 한 겹 안쪽에 세운다. 실루엣은 구체가
      그대로 쥐고, 이 겹은 «뒤가 비치지 않게» 만 한다. 색은 구체의 바탕빛을 어둡게 깐 것이라
      가장자리에서 두 겹이 만나도 색이 튀지 않는다.
    */
    const core = background || !withOrb
      ? null
      : new THREE.Mesh(sphereGeo, new THREE.MeshBasicMaterial({ color: 0x000000 }));
    if (core) {
      /*
        <b>티끌 쉘보다 안쪽</b>이어야 한다. 알갱이는 반지름 2.95 에서 태어나 바깥으로 흩어지는데
        (`OrbParticles`), 이 겹을 2.955(=3×0.985)로 세웠더니 <b>알갱이가 전부 이 뒤로 숨었다</b> —
        구체가 매끈한 공 하나가 되어 버렸다(사용자 지적 2026-09-17). 0.9 이면 2.7 이라 티끌이
        전부 앞에 서고, 구체 껍질(3)과의 틈은 화면에서 한두 px 이라 비침이 되살아나지 않는다.
      */
      core.scale.setScalar(3 * 0.9);
      scene.add(core);
    }

    // ── 입자 두 겹. 조명 위치는 `Global Uniforms` 실측값.
    /* 입자 셰이더의 `vColorMix` 는 «태양까지의 거리» 를 `uSunDistanceColorStep 9.93` 로
       자른다. 전역 광원(-37,23,91)을 넣으면 거리가 102 라 항상 1 로 포화돼 붉은 얼룩이
       통째로 사라진다. 씬 파일의 `Sun and Shadow.sunPosition` 이 맞는 값이다(거리 9.2). */
    const lightPos = new THREE.Vector3(4.2, 5.799999999999996, 6.00000000000003);
    const lightPos2 = new THREE.Vector3(-10.000000000000005, 1.1999999999999993, -0.1999999999999064);
    const shared = {
      renderer,
      scene,
      camera,
      cloudsTexture: cloudsTarget.texture,
      fluidTexture,
      lightPosition: lightPos,
      lightPosition2: lightPos2,
    };
    const tint = look.particleTint ? hex(look.particleTint) : null;
    const dust = particles
      ? createOrbParticles({ ...shared, tint, size: particleSize, grid: particleGrid })
      : null;
    // 섬광은 «다른 시뮬레이션» 이다 — 속도 판이 없고 고리에서 태어난다(OrbFlare). 그래서 따로 끈다.
    const flareFx = flare ? createOrbFlare({ renderer, scene, camera, fluidTexture, tint }) : null;

    // ── Post FX. 원본 bloom { strength .33, radius 0 } + 마지막 OutputPass.
    const composer = new EffectComposer(renderer);
    const renderPass = new RenderPass(scene, camera);
    composer.addPass(renderPass);
    // strength·radius 는 원본 값. threshold 는 씬 파일에 없어 기본값을 쓴다.
    /* threshold 는 씬 파일에 없다. 배경이 평면일 때는 배경까지 번져 1.0 으로 올렸었지만,
       스카이돔으로 바꾼 뒤로는 그럴 일이 없다. 기본값으로 두어 섬광이 제대로 빛나게 한다. */
    const bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.32999999999999985, 0, 0.85);
    /*
      <b>bloom 은 알파를 죽인다</b>(2026-09-17). 블러 셰이더가 `gl_FragColor = vec4(색, 1.0)` 로
      끝나고, 그 결과를 <b>가산 합성</b>으로 원본 버퍼에 얹는다 — 가산은 알파도 더하므로 텅 빈
      픽셀까지 알파가 1 이 된다. 배경이 있는 히어로에서는 티가 안 난다(돔이 화면을 채운다).
      돔을 끄면 그대로 드러난다: 구체 둘레에 <b>검은 네모</b>가 남는다.

      <p>한 번은 bloom 을 빼서 해결했는데, 그러면 <b>알갱이가 안 보인다</b>(사용자 지적) — 티끌
      하나하나는 작고 어두워서 <b>빛 번짐이 있어야</b> 읽힌다. 그것이 구체의 살결이다.

      <p>그래서 빼는 대신 <b>섞는 방법을 바꾼다</b>: 색(RGB)은 그대로 더하고 알파는 <b>바탕의 것을
      그대로 둔다</b>(`blendSrcAlpha = Zero`, `blendDstAlpha = One`). 구체 위에서는 알파가 1 이라
      번짐이 그대로 보이고, 구체 밖은 알파가 0 이라 번짐이 «투명한 빛» 이 되어 사라진다 —
      바깥으로 퍼지는 후광은 잃지만, 애초에 여기서는 둘레 섬광을 끈 자리다.
    */
    if (!background) {
      const blend = bloom.blendMaterial;
      blend.blending = THREE.CustomBlending;
      blend.blendEquation = THREE.AddEquation;
      blend.blendSrc = THREE.SrcAlphaFactor;
      blend.blendDst = THREE.OneFactor;
      blend.blendEquationAlpha = THREE.AddEquation;
      blend.blendSrcAlpha = THREE.ZeroFactor;
      blend.blendDstAlpha = THREE.OneFactor;
    }
    composer.addPass(bloom);
    const outputPass = new OutputPass();
    /* 마지막 사각형도 투명해야 한다 — 기본값(`transparent:false`)이면 알파 1 로 덮어쓴다.
       톤매핑·sRGB 변환은 이 패스만 하므로 «빼고 직접 그리기» 로 대신할 수 없다(머리말 §노출). */
    if (!background) outputPass.material.transparent = true;
    composer.addPass(outputPass);


    const resize = (width: number, height: number, pixelRatio: number) => {
      const w = Math.max(1, Math.round(width));
      const h = Math.max(1, Math.round(height));
      const pr = pixelRatio;
      renderer.setSize(w, h, false);
      /* composer 가 픽셀 비율을 스스로 곱해 <b>모든 패스</b>를 그 크기로 맞춘다(EffectComposer.setSize
         → pass.setSize(w × pr, h × pr)). 그래서 여기서 bloom 을 한 번 더 부르면 안 된다 — 그 호출이
         방금 맞춘 크기를 CSS 픽셀로 되돌려, dpr 2 인 화면에서 <b>번짐만 절반 해상도</b>로 계산된다.
         앱(pr=1)에서는 두 값이 같아 티가 안 났고, 웹에서만 구슬 테두리가 거칠어 보였다(2026-09-18). */
      composer.setSize(w, h);
      cloudsTarget.setSize(Math.round(w * pr), Math.round(h * pr));
      camera.aspect = w / h;

      /* 구체를 «정해진 px» 로 세운다. 화면에 담기는 세계 높이가 (지름 × 칸높이 ÷ 목표px)면
         구체가 딱 그만큼 차지한다. 그 높이를 담는 화각을 거꾸로 낸다.

         칸이 아주 낮을 때는 목표를 그대로 쓸 수 없다 — 480px 짜리 구체를 300px 칸에 세우면
         화각이 좁아져 구체가 칸을 넘어선다. 칸 높이의 72% 를 넘지 않게 눌러 둔다. */
      const target = orbSizeRef.current;
      if (target) {
        const visible = ORB_WORLD_DIAMETER * (h / Math.min(target, h * 0.72));
        const distance = camera.position.length();
        camera.fov = THREE.MathUtils.clamp(
          THREE.MathUtils.radToDeg(2 * Math.atan(visible / (2 * distance))),
          10,
          100,
        );
      }
      camera.updateProjectionMatrix();
      bgUniforms.uResolution.value.set(w * pr, h * pr);
    };
    resize(1, 1, 1);

    let lastSig = '';
    const applyLook = () => {
      const l = lookRef.current;
      const sig = [
        l.bg.color1, l.bg.color2, l.bg.color3, l.bg.color4, l.bg.color3Brightness,
        l.orb.ambient, l.orb.clouds, l.orb.fresnel, l.orb.light, l.orb.light2,
        l.particleTint,
      ].join('|');
      if (sig === lastSig) return;
      lastSig = sig;

      bgUniforms.uColor1.value.copy(hex(l.bg.color1));
      bgUniforms.uColor2.value.copy(hex(l.bg.color2));
      bgUniforms.uColor3.value.copy(hex(l.bg.color3));
      bgUniforms.uColor4.value.copy(hex(l.bg.color4));
      bgUniforms.uColor3Brightness.value = l.bg.color3Brightness;

      if (!orb) return;
      orbUniforms.uAmbientColor.value.copy(hex(l.orb.ambient));
      /* 알맹이는 바탕빛을 어둡게 깐 색이다 — 구체가 반투명해 이 색이 비쳐 나오므로, 검정으로
         두면 구체 전체가 한 단 어두워진다. 같은 계열이어야 두 겹이 한 몸으로 읽힌다. */
      (core?.material as THREE.MeshBasicMaterial | undefined)?.color.copy(hex(l.orb.ambient)).multiplyScalar(0.22);
      orbUniforms.uCloudsColor.value.copy(hex(l.orb.clouds));
      orbUniforms.uFresnelColor.value.copy(hex(l.orb.fresnel));
      orbUniforms.uLightColor.value.copy(hex(l.orb.light));
      orbUniforms.uLightColor2.value.copy(hex(l.orb.light2));

      const next = l.particleTint ? hex(l.particleTint) : null;
      dust?.setTint(next);
      flareFx?.setTint(next);
    };

    /* 예열 프레임 수 — 60fps 로 0.75 초어치. 입자 시뮬만 돌리므로(256²·96² 판 두 장)
       화면 렌더보다 훨씬 싸고, 한 프레임 안에서 끝난다. */
    const PREROLL = 45;
    const clock = new THREE.Clock();
    let frames = 0;

    return {
      resize,
      setPointer: (x: number, y: number, aspect: number) => fluid.setPointer(x, y, aspect),
      setLook: (next: HeroLook) => {
        lookRef.current = next;
      },
      setOrbSize: (next?: number) => {
        orbSizeRef.current = next;
      },
      get frames() {
        return frames;
      },
      frame: (dtMs: number) => {
        const t = clock.getElapsedTime();
        applyLook();

        /* 첫 프레임을 그대로 보여 주면 «껍질» 이 한 번 번진다 — setup 직후에는 입자 65,536 개가
           반지름 2.95 에 똑같이 붙어 있어 한 덩어리로 뭉쳐 있고, 그 위에 bloom 이 얹혀 흰빛으로
           퍼진 뒤에야 컬 노이즈를 타고 흩어진다. 흩어진 «뒤» 부터 보여 주면 그 번짐이 없다. */
        if (frames === 0 && dust) {
          for (let i = 1; i <= PREROLL; i++) {
            dust.update(t + i / 60, 16.666);
            flareFx?.update(t + i / 60, 16.666);
          }
        }

        fluid.step(dtMs);
        bgUniforms.tFluidSim.value = fluid.texture;
        bgUniforms.uTime.value = t;
        cloudsUniforms.uTime.value = t;
        orbUniforms.uTime.value = t;

        if (orb) {
          orb.rotation.y = t * 0.06;
          cloudsMesh.rotation.copy(orb.rotation);
        }

        dust?.update(t, dtMs);
        flareFx?.update(t, dtMs);

        renderer.setRenderTarget(cloudsTarget);
        renderer.clear();
        renderer.render(cloudsScene, camera);
        renderer.setRenderTarget(null);
        composer.render();

        frames += 1;
      },
      dispose: () => {
        dust?.dispose();
        flareFx?.dispose();
        fluid.dispose();
        composer.dispose();
        cloudsTarget.dispose();
        sphereGeo.dispose();
        bgMesh?.geometry.dispose();
        if (bgMesh) (bgMesh.material as THREE.Material).dispose();
        if (orb) (orb.material as THREE.Material).dispose();
        if (core) (core.material as THREE.Material).dispose();
        (cloudsMesh.material as THREE.Material).dispose();
        renderer.dispose();
        renderer.domElement.remove();
      },
    };
}

